import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { calculateToplamKar, convertCurrency, formatCurrencyAmount, formatProfitMargin } from '../costUtils'
import { Category, CURRENCY_SYMBOLS, PriceListRow, SalesChannel, SUPPORTED_CURRENCIES, Tenant } from '../types'

interface Props {
  salesChannels: SalesChannel[]
  categories?: Category[]
  tenant?: Tenant | null
}

interface MarginEditState {
  row: PriceListRow
  salesChannelId: string
  salesChannelName?: string
  parameterId: string
  label: string
  currentValue: number | null
  defaultValue: number | null
  isOverridden: boolean
  isApplicable: boolean
}

interface IntermediateColumnDef {
  key: string
  header: string
  channelId: string
  channelName: string
  type: 'margin' | 'price'
  parameterId?: string
  parameterLabel?: string
}

function PencilIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

function GearIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

function PowerIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </svg>
  )
}

export function PriceListView({ salesChannels, categories = [], tenant }: Props) {
  const [selectedChannelId, setSelectedChannelId] = useState<string>('')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<'active' | 'passive'>('active')
  const [actionProcessingId, setActionProcessingId] = useState<string | null>(null)
  const [priceList, setPriceList] = useState<PriceListRow[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [exporting, setExporting] = useState<boolean>(false)

  // Column visibility modal
  const [showColumnModal, setShowColumnModal] = useState<boolean>(false)
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>({})

  // Single cell margin override modal
  const [marginEdit, setMarginEdit] = useState<MarginEditState | null>(null)
  const [marginEditInput, setMarginEditInput] = useState<string>('')

  // Bulk margin override modal
  const [showBulkMarginModal, setShowBulkMarginModal] = useState<boolean>(false)
  const [bulkParameterId, setBulkParameterId] = useState<string>('')
  const [bulkMode, setBulkMode] = useState<'set' | 'reset'>('set')
  const [bulkMarginValue, setBulkMarginValue] = useState<string>('20')
  const [bulkProcessing, setBulkProcessing] = useState<boolean>(false)

  useEffect(() => {
    if (salesChannels.length > 0) {
      if (!selectedChannelId || !salesChannels.some((sc) => sc.id === selectedChannelId)) {
        setSelectedChannelId(salesChannels[0].id)
      }
    } else {
      setSelectedChannelId('')
      setPriceList([])
    }
  }, [salesChannels, selectedChannelId])

  const selectedChannel = useMemo(
    () => salesChannels.find((sc) => sc.id === selectedChannelId),
    [salesChannels, selectedChannelId],
  )

  const marginParams = useMemo(() => {
    if (!selectedChannel) return []
    return [...(selectedChannel.parameters || [])]
      .filter((p) => p.parameter_type === 'profit_margin')
      .sort((a, b) => a.order_index - b.order_index)
  }, [selectedChannel])

  const fetchPriceList = async (channelId: string, status: 'active' | 'passive' = statusFilter) => {
    if (!channelId) {
      setPriceList([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await api.getPriceList(channelId, status)
      setPriceList(data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedChannelId) {
      fetchPriceList(selectedChannelId, statusFilter)
    } else {
      setPriceList([])
    }
    setSelectedIds([])
  }, [selectedChannelId, statusFilter])

  // Derive intermediate ancestor columns from channel_steps of the first row
  const intermediateColumns = useMemo<IntermediateColumnDef[]>(() => {
    if (priceList.length === 0 || !priceList[0].channel_steps) return []
    const steps = priceList[0].channel_steps
    const cols: IntermediateColumnDef[] = []

    for (const step of steps) {
      for (const pm of step.profit_margins) {
        cols.push({
          key: `margin-${step.sales_channel_id}-${pm.parameter_id}`,
          header: `${step.sales_channel_name} ${pm.label}`,
          channelId: step.sales_channel_id,
          channelName: step.sales_channel_name,
          type: 'margin',
          parameterId: pm.parameter_id,
          parameterLabel: pm.label,
        })
      }
      cols.push({
        key: `price-${step.sales_channel_id}`,
        header: `${step.sales_channel_name} Fiyatı`,
        channelId: step.sales_channel_id,
        channelName: step.sales_channel_name,
        type: 'price',
      })
    }

    return cols
  }, [priceList])

  const preferredCurr = selectedChannel?.preferred_currency || 'TL'
  const altCurrencies = useMemo(
    () => SUPPORTED_CURRENCIES.filter((c) => c !== preferredCurr),
    [preferredCurr],
  )
  const effectiveRates = selectedChannel?.effective_exchange_rates || null
  const channelIncludesKdv = selectedChannel?.include_kdv !== false

  const allColumns = useMemo(() => {
    const cols: { key: string; label: string }[] = [
      { key: 'name', label: 'Ürün Adı' },
      { key: 'code', label: 'Ürün Kodu' },
      { key: 'base_cost', label: 'Taban Maliyet' },
    ]
    altCurrencies.forEach((c) => {
      cols.push({
        key: `base_cost_${c}`,
        label: `Taban Maliyet (${CURRENCY_SYMBOLS[c] || c})`,
      })
    })

    cols.push({ key: 'genel_maliyet', label: 'Genel Maliyet' })
    altCurrencies.forEach((c) => {
      cols.push({
        key: `genel_maliyet_${c}`,
        label: `Genel Maliyet (${CURRENCY_SYMBOLS[c] || c})`,
      })
    })

    intermediateColumns.forEach((col) => {
      cols.push({
        key: `intermediate_${col.key}`,
        label: col.header,
      })
      if (col.type === 'price') {
        altCurrencies.forEach((c) => {
          cols.push({
            key: `intermediate_${col.key}_${c}`,
            label: `${col.header} (${CURRENCY_SYMBOLS[c] || c})`,
          })
        })
      }
    })

    marginParams.forEach((mp) => {
      cols.push({
        key: `margin_${mp.id || mp.label}`,
        label: mp.label,
      })
    })

    cols.push({ key: 'toplam_kar', label: 'Toplam Kar' })

    const channelIncludesKdv = selectedChannel?.include_kdv !== false

    if (channelIncludesKdv) {
      cols.push({ key: 'kdv_haric', label: 'KDV Hariç Satış Fiyatı' })
      altCurrencies.forEach((c) => {
        cols.push({
          key: `kdv_haric_${c}`,
          label: `KDV Hariç Satış Fiyatı (${CURRENCY_SYMBOLS[c] || c})`,
        })
      })

      cols.push({ key: 'kdv_dahil', label: 'KDV Dahil Satış Fiyatı' })
      altCurrencies.forEach((c) => {
        cols.push({
          key: `kdv_dahil_${c}`,
          label: `KDV Dahil Satış Fiyatı (${CURRENCY_SYMBOLS[c] || c})`,
        })
      })
    } else {
      cols.push({ key: 'kdv_haric', label: 'Satış Fiyatı' })
      altCurrencies.forEach((c) => {
        cols.push({
          key: `kdv_haric_${c}`,
          label: `Satış Fiyatı (${CURRENCY_SYMBOLS[c] || c})`,
        })
      })
    }

    cols.push({ key: 'final_price', label: 'Final Satış Fiyatı' })
    altCurrencies.forEach((c) => {
      cols.push({
        key: `final_price_${c}`,
        label: `Final Satış Fiyatı (${CURRENCY_SYMBOLS[c] || c})`,
      })
    })

    if (channelIncludesKdv) {
      cols.push({ key: 'kdv_rate', label: 'KDV Oranı' })
    }
    return cols
  }, [intermediateColumns, marginParams, altCurrencies, selectedChannel])

  const isAltCurrencyCol = (key: string): boolean => {
    return SUPPORTED_CURRENCIES.some((curr) => key.endsWith(`_${curr}`))
  }

  const isColVisible = (key: string): boolean => {
    if (key in hiddenColumns) {
      return !hiddenColumns[key]
    }
    return !isAltCurrencyCol(key)
  }

  const handleToggleColumn = (key: string) => {
    const currentlyVisible = isColVisible(key)
    setHiddenColumns((prev) => ({
      ...prev,
      [key]: currentlyVisible,
    }))
  }

  const handleShowAllColumns = () => {
    const allHidden: Record<string, boolean> = {}
    allColumns.forEach((c) => {
      allHidden[c.key] = false
    })
    setHiddenColumns(allHidden)
  }

  const handleHideAllColumns = () => {
    const allHidden: Record<string, boolean> = {}
    allColumns.forEach((c) => {
      allHidden[c.key] = true
    })
    setHiddenColumns(allHidden)
  }

  const visibleColumnCount = useMemo(() => {
    if (statusFilter === 'passive') return 3
    return 1 + allColumns.filter((c) => isColVisible(c.key)).length + 1 // +1 checkbox, +1 İŞLEM
  }, [allColumns, hiddenColumns, statusFilter])

  // Calculate minimum width dynamically based on visible columns
  const tableMinWidth = useMemo(() => {
    if (statusFilter === 'passive') return 520
    let width = 40 // checkbox column
    if (isColVisible('name')) width += 160
    if (isColVisible('code')) width += 110
    if (isColVisible('base_cost')) width += 105
    if (isColVisible('genel_maliyet')) width += 105

    altCurrencies.forEach((c) => {
      if (isColVisible(`base_cost_${c}`)) width += 105
      if (isColVisible(`genel_maliyet_${c}`)) width += 105
    })

    intermediateColumns.forEach((col) => {
      if (isColVisible(`intermediate_${col.key}`)) {
        width += col.type === 'margin' ? 140 : 130
      }
      if (col.type === 'price') {
        altCurrencies.forEach((c) => {
          if (isColVisible(`intermediate_${col.key}_${c}`)) {
            width += 130
          }
        })
      }
    })

    marginParams.forEach((mp) => {
      if (isColVisible(`margin_${mp.id || mp.label}`)) {
        width += 125
      }
    })

    const channelIncludesKdv = selectedChannel?.include_kdv !== false

    if (isColVisible('toplam_kar')) width += 110
    if (isColVisible('kdv_haric')) width += 115
    if (channelIncludesKdv && isColVisible('kdv_dahil')) width += 115
    if (isColVisible('final_price')) width += 115
    if (channelIncludesKdv && isColVisible('kdv_rate')) width += 70

    altCurrencies.forEach((c) => {
      if (isColVisible(`kdv_haric_${c}`)) width += 115
      if (channelIncludesKdv && isColVisible(`kdv_dahil_${c}`)) width += 115
      if (isColVisible(`final_price_${c}`)) width += 115
    })

    width += 65 // İŞLEM column (compact power icon button)
    return width
  }, [allColumns, hiddenColumns, intermediateColumns, marginParams, altCurrencies, statusFilter, selectedChannel])

  const getRowMethod = (row: PriceListRow): string => {
    return row.calculation_method || tenant?.default_calculation_method || 'cost_plus'
  }

  const getRowToplamKar = (row: PriceListRow): number | null => {
    const genelMaliyet = Number(row.genel_maliyet ?? 0)
    const kdvHaric = Number(row.kdv_haric_satis_fiyati ?? 0)
    const method = getRowMethod(row)
    return calculateToplamKar(genelMaliyet, kdvHaric, method)
  }

  const filteredPriceList = useMemo(() => {
    return priceList.filter((row) => {
      const matchCat = !selectedCategoryId || row.category_id === selectedCategoryId
      const term = searchTerm.trim().toLowerCase()
      const matchSearch =
        !term ||
        (row.product_name && row.product_name.toLowerCase().includes(term)) ||
        (row.product_code && row.product_code.toLowerCase().includes(term))
      return matchCat && matchSearch
    })
  }, [priceList, selectedCategoryId, searchTerm])

  const visibleSelectedIds = useMemo(() => {
    const visibleSet = new Set(filteredPriceList.map((r) => r.product_id))
    return selectedIds.filter((id) => visibleSet.has(id))
  }, [filteredPriceList, selectedIds])

  const isAllVisibleSelected =
    filteredPriceList.length > 0 && visibleSelectedIds.length === filteredPriceList.length
  const isSomeVisibleSelected =
    visibleSelectedIds.length > 0 && !isAllVisibleSelected

  const handleToggleSelectAll = () => {
    if (isAllVisibleSelected) {
      const visibleSet = new Set(filteredPriceList.map((r) => r.product_id))
      setSelectedIds((prev) => prev.filter((id) => !visibleSet.has(id)))
    } else {
      const newIds = new Set([...selectedIds, ...filteredPriceList.map((r) => r.product_id)])
      setSelectedIds(Array.from(newIds))
    }
  }

  const handleToggleRow = (productId: string) => {
    if (selectedIds.includes(productId)) {
      setSelectedIds(selectedIds.filter((id) => id !== productId))
    } else {
      setSelectedIds([...selectedIds, productId])
    }
  }

  const handleExportExcel = async () => {
    if (!selectedChannelId) return
    setExporting(true)
    try {
      const selectedRows = filteredPriceList.filter((p) => selectedIds.includes(p.product_id))
      const productIds =
        selectedIds.length > 0
          ? selectedRows.filter((r) => r.item_type === 'product').map((r) => r.product_id)
          : undefined
      const tradingGoodIds =
        selectedIds.length > 0
          ? selectedRows.filter((r) => r.item_type === 'trading_good').map((r) => r.product_id)
          : undefined

      await api.exportPriceListExcel(selectedChannelId, productIds, tradingGoodIds)
    } catch (err: any) {
      alert(`Excel indirme hatası: ${err.message}`)
    } finally {
      setExporting(false)
    }
  }

  const handleOpenMarginEdit = (
    row: PriceListRow,
    salesChannelId: string,
    salesChannelName: string | undefined,
    parameterId: string,
    label: string,
    currentValue: number | null,
    defaultValue: number | null,
    isOverridden: boolean,
    isApplicable: boolean,
  ) => {
    setMarginEdit({
      row,
      salesChannelId,
      salesChannelName,
      parameterId,
      label,
      currentValue,
      defaultValue,
      isOverridden,
      isApplicable,
    })
    setMarginEditInput(currentValue !== null ? (currentValue * 100).toFixed(1) : '')
  }

  const handleSaveMarginEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!marginEdit || !selectedChannelId) return
    try {
      const numericVal = parseFloat(marginEditInput)
      if (isNaN(numericVal) || numericVal < 0 || numericVal >= 100) {
        alert('Lütfen 0 ile 100 arasında geçerli bir kâr marjı yüzdesi girin.')
        return
      }
      await api.overridePriceListMargin({
        sales_channel_id: marginEdit.salesChannelId,
        parameter_id: marginEdit.parameterId,
        item_type: marginEdit.row.item_type,
        product_id: marginEdit.row.product_id,
        value: numericVal / 100,
      })
      setMarginEdit(null)
      await fetchPriceList(selectedChannelId)
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleResetSingleMargin = async () => {
    if (!marginEdit || !selectedChannelId) return
    try {
      await api.overridePriceListMargin({
        sales_channel_id: marginEdit.salesChannelId,
        parameter_id: marginEdit.parameterId,
        item_type: marginEdit.row.item_type,
        product_id: marginEdit.row.product_id,
        value: null,
      })
      setMarginEdit(null)
      await fetchPriceList(selectedChannelId)
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleOpenBulkModal = () => {
    if (marginParams.length > 0) {
      setBulkParameterId(marginParams[0].id || '')
    }
    setBulkMode('set')
    setBulkMarginValue('25')
    setShowBulkMarginModal(true)
  }

  const handleApplyBulkMargin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedChannelId || !bulkParameterId) return
    const targetRows = filteredPriceList.filter((r) => selectedIds.includes(r.product_id))
    if (targetRows.length === 0) return

    setBulkProcessing(true)
    try {
      let val: number | null = null
      if (bulkMode === 'set') {
        const num = parseFloat(bulkMarginValue)
        if (isNaN(num) || num < 0 || num >= 100) {
          alert('Lütfen 0 ile 100 arasında geçerli bir yüzde girin.')
          setBulkProcessing(false)
          return
        }
        val = num / 100
      }

      await api.bulkOverridePriceListMargins({
        sales_channel_id: selectedChannelId,
        parameter_id: bulkParameterId,
        items: targetRows.map((r) => ({
          item_type: r.item_type,
          product_id: r.product_id,
        })),
        value: val,
      })

      setShowBulkMarginModal(false)
      setSelectedIds([])
      await fetchPriceList(selectedChannelId)
    } catch (err: any) {
      alert(err.message)
    } finally {
      setBulkProcessing(false)
    }
  }

  const handleUnassignProduct = async (row: PriceListRow) => {
    if (!selectedChannelId) return
    setActionProcessingId(row.product_id)
    try {
      await api.unassignProductFromSalesChannel(selectedChannelId, {
        product_id: row.product_id,
        item_type: row.item_type,
      })
      await fetchPriceList(selectedChannelId, statusFilter)
    } catch (err: any) {
      alert(err.message || 'Ürün pasifleştirilemedi.')
    } finally {
      setActionProcessingId(null)
    }
  }

  const handleAssignProduct = async (row: PriceListRow) => {
    if (!selectedChannelId) return
    setActionProcessingId(row.product_id)
    try {
      await api.assignProductToSalesChannel(selectedChannelId, {
        product_id: row.product_id,
        item_type: row.item_type,
      })
      await fetchPriceList(selectedChannelId, statusFilter)
    } catch (err: any) {
      alert(err.message || 'Ürün aktifleştirilemedi.')
    } finally {
      setActionProcessingId(null)
    }
  }

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Fiyat Listeleri</h2>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {statusFilter === 'active' && visibleSelectedIds.length > 0 && marginParams.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleOpenBulkModal}
              title="Seçili ürünlerin kâr marjlarını toplu düzenle"
            >
              Toplu Kâr Marjı Düzenle ({visibleSelectedIds.length})
            </button>
          )}

          {statusFilter === 'active' && (
            <button
              className="btn btn-secondary"
              onClick={handleExportExcel}
              disabled={exporting || filteredPriceList.length === 0 || !selectedChannelId}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>
                {exporting
                  ? 'İndiriliyor…'
                  : visibleSelectedIds.length > 0
                  ? `Excel İndir (${visibleSelectedIds.length} Seçili)`
                  : 'Excel İndir (Tümü)'}
              </span>
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div
        className="card filter-card"
        style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}
      >
        <div className="filter-group">
          <label htmlFor="sales-channel-filter">
            <strong>Satış Kanalı:</strong>
          </label>
          <select
            id="sales-channel-filter"
            className="form-control"
            value={selectedChannelId}
            onChange={(e) => setSelectedChannelId(e.target.value)}
          >
            {salesChannels.length === 0 ? (
              <option value="">(Tanımlı Satış Kanalı Yok)</option>
            ) : (
              salesChannels.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} {sc.code ? `(${sc.code})` : ''}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="filter-group">
          <label>
            <strong>Durum:</strong>
          </label>
          <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
            <button
              type="button"
              className={statusFilter === 'active' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{
                borderRadius: 0,
                border: 'none',
                padding: '0.4rem 0.95rem',
                fontSize: '0.875rem',
                fontWeight: statusFilter === 'active' ? 600 : 400,
              }}
              onClick={() => setStatusFilter('active')}
            >
              Aktif
            </button>
            <button
              type="button"
              className={statusFilter === 'passive' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{
                borderRadius: 0,
                border: 'none',
                borderLeft: '1px solid #d1d5db',
                padding: '0.4rem 0.95rem',
                fontSize: '0.875rem',
                fontWeight: statusFilter === 'passive' ? 600 : 400,
              }}
              onClick={() => setStatusFilter('passive')}
            >
              Pasif
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label htmlFor="category-filter">
            <strong>Kategori:</strong>
          </label>
          <select
            id="category-filter"
            className="form-control"
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group" style={{ flex: 1, minWidth: '240px' }}>
          <label htmlFor="price-list-search">
            <strong>Ürün Ara:</strong>
          </label>
          <input
            id="price-list-search"
            type="text"
            className="form-control"
            placeholder="Ürün adı veya koduna göre ara…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Main Table Container: Scrollable horizontally without squishing */}
      <div
        className="table-card"
        style={{
          marginTop: '1rem',
          overflowX: 'auto',
          overflowY: 'hidden',
          maxWidth: '100%',
        }}
      >
        {salesChannels.length === 0 ? (
          <div style={{ padding: '3.5rem 1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-color, #1f2937)', marginBottom: '0.5rem' }}>
              Henüz tanımlı bir satış kanalı bulunmamaktadır.
            </div>
            <p className="text-muted" style={{ maxWidth: '480px', margin: '0 auto 1.25rem auto', fontSize: '0.9rem' }}>
              Fiyat listeleri ve nihai satış fiyatları satış kanalları üzerinden hesaplanır. Fiyatları görüntülemek için lütfen en az bir satış kanalı ekleyin.
            </p>
          </div>
        ) : loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>Fiyatlar hesaplanıyor...</div>
        ) : (
          <table
            className="data-table price-list-table"
            style={{ minWidth: `${tableMinWidth}px`, width: '100%' }}
          >
            <thead>
              {statusFilter === 'passive' ? (
                <tr>
                  <th className="col-name" style={{ minWidth: '220px' }}>
                    ÜRÜN ADI
                  </th>
                  <th className="col-code" style={{ minWidth: '160px' }}>
                    ÜRÜN KODU
                  </th>
                  <th className="col-action" style={{ width: '140px', minWidth: '140px', textAlign: 'center' }}>
                    İŞLEM
                  </th>
                </tr>
              ) : (
                <tr>
                  <th
                    className="col-checkbox"
                    style={{
                      width: '40px',
                      minWidth: '40px',
                      textAlign: 'center',
                      verticalAlign: 'bottom',
                      padding: '0.35rem 0.25rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setShowColumnModal(true)}
                        title="Sütun Görünürlüğü"
                        aria-label="Sütun görünürlüğünü ayarla"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '2px',
                          borderRadius: '4px',
                          color: '#6b7280',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#111827'
                          e.currentTarget.style.background = '#e5e7eb'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#6b7280'
                          e.currentTarget.style.background = 'transparent'
                        }}
                      >
                        <GearIcon size={14} />
                      </button>
                      <input
                        type="checkbox"
                        checked={isAllVisibleSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isSomeVisibleSelected
                        }}
                        onChange={handleToggleSelectAll}
                        title="Filtrelenen Ürünleri Seç / Kaldır"
                        aria-label="Filtrelenen tüm ürünleri seç"
                      />
                    </div>
                  </th>
                  <th className="col-action" style={{ width: '65px', minWidth: '65px', textAlign: 'center' }}>
                    İŞLEM
                  </th>
                  {isColVisible('name') && (
                    <th className="col-name" style={{ minWidth: '160px' }}>
                      Ürün<br />Adı
                    </th>
                  )}
                  {isColVisible('code') && (
                    <th className="col-code" style={{ minWidth: '110px' }}>
                      Ürün<br />Kodu
                    </th>
                  )}
                  {isColVisible('base_cost') && (
                    <th className="col-cost" style={{ minWidth: '105px' }}>
                      Taban<br />Maliyet
                    </th>
                  )}
                  {altCurrencies.map(
                    (c) =>
                      isColVisible(`base_cost_${c}`) && (
                        <th key={`base_cost_${c}`} className="col-cost" style={{ minWidth: '105px' }}>
                          Taban<br />Maliyet ({CURRENCY_SYMBOLS[c] || c})
                        </th>
                      ),
                  )}
                  {isColVisible('genel_maliyet') && (
                    <th className="col-cost" style={{ minWidth: '105px' }}>
                      Genel<br />Maliyet
                    </th>
                  )}
                  {altCurrencies.map(
                    (c) =>
                      isColVisible(`genel_maliyet_${c}`) && (
                        <th key={`genel_maliyet_${c}`} className="col-cost" style={{ minWidth: '105px' }}>
                          Genel<br />Maliyet ({CURRENCY_SYMBOLS[c] || c})
                        </th>
                      ),
                  )}

                  {/* Dynamically render intermediate ancestor channels columns */}
                  {intermediateColumns.map((col) => {
                    const isMainVis = isColVisible(`intermediate_${col.key}`)
                    return (
                      <React.Fragment key={col.key}>
                        {isMainVis && (
                          <th
                            className={col.type === 'margin' ? 'col-margin' : 'col-price'}
                            style={{ minWidth: col.type === 'margin' ? '140px' : '130px' }}
                          >
                            {col.header}
                          </th>
                        )}
                        {col.type === 'price' &&
                          altCurrencies.map(
                            (c) =>
                              isColVisible(`intermediate_${col.key}_${c}`) && (
                                <th
                                  key={`intermediate_${col.key}_${c}`}
                                  className="col-price"
                                  style={{ minWidth: '130px' }}
                                >
                                  {col.header} ({CURRENCY_SYMBOLS[c] || c})
                                </th>
                              ),
                          )}
                      </React.Fragment>
                    )
                  })}

                  {/* Current channel profit margin columns (unprefixed) */}
                  {marginParams
                    .filter((mp) => isColVisible(`margin_${mp.id || mp.label}`))
                    .map((mp) => (
                      <th key={mp.id || mp.label} className="col-margin" style={{ minWidth: '125px' }}>
                        {mp.label}
                      </th>
                    ))}

                  {isColVisible('toplam_kar') && (
                    <th className="col-margin" style={{ minWidth: '110px' }}>
                      Toplam<br />Kar
                    </th>
                  )}
                  {isColVisible('kdv_haric') && (
                    <th className="col-price" style={{ minWidth: '115px' }}>
                      {channelIncludesKdv ? (
                        <>KDV Hariç<br />Satış Fiyatı</>
                      ) : (
                        <>Satış<br />Fiyatı</>
                      )}
                    </th>
                  )}
                  {altCurrencies.map(
                    (c) =>
                      isColVisible(`kdv_haric_${c}`) && (
                        <th key={`kdv_haric_${c}`} className="col-price" style={{ minWidth: '115px' }}>
                          {channelIncludesKdv ? 'KDV Hariç' : ''}<br />Satış Fiyatı ({CURRENCY_SYMBOLS[c] || c})
                        </th>
                      ),
                  )}
                  {channelIncludesKdv && isColVisible('kdv_dahil') && (
                    <th className="col-price" style={{ minWidth: '115px' }}>
                      KDV Dahil<br />Satış Fiyatı
                    </th>
                  )}
                  {channelIncludesKdv && altCurrencies.map(
                    (c) =>
                      isColVisible(`kdv_dahil_${c}`) && (
                        <th key={`kdv_dahil_${c}`} className="col-price" style={{ minWidth: '115px' }}>
                          KDV Dahil<br />Satış Fiyatı ({CURRENCY_SYMBOLS[c] || c})
                        </th>
                      ),
                  )}
                  {isColVisible('final_price') && (
                    <th className="col-price" style={{ minWidth: '115px' }}>
                      Final<br />Satış Fiyatı
                    </th>
                  )}
                  {altCurrencies.map(
                    (c) =>
                      isColVisible(`final_price_${c}`) && (
                        <th key={`final_price_${c}`} className="col-price" style={{ minWidth: '115px' }}>
                          Final<br />Satış Fiyatı ({CURRENCY_SYMBOLS[c] || c})
                        </th>
                      ),
                  )}
                  {channelIncludesKdv && isColVisible('kdv_rate') && (
                    <th className="col-kdv" style={{ minWidth: '70px' }}>
                      KDV<br />Oranı
                    </th>
                  )}
                </tr>
              )}
            </thead>
            <tbody>
              {filteredPriceList.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="text-center empty-state" style={{ padding: '2.5rem 1rem' }}>
                    {statusFilter === 'passive'
                      ? 'Bu satış kanalında pasif ürün bulunamadı.'
                      : 'Hesaplanacak ürün bulunamadı.'}
                  </td>
                </tr>
              ) : statusFilter === 'passive' ? (
                filteredPriceList.map((row) => (
                  <tr key={row.product_id}>
                    <td className="col-name">
                      <strong>{row.product_name}</strong>
                    </td>
                    <td className="col-code">
                      <code>{row.product_code}</code>
                    </td>
                    <td className="col-action" style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={actionProcessingId === row.product_id}
                        onClick={() => handleAssignProduct(row)}
                        title="Ürünü bu satış kanalına aktifleştir"
                      >
                        {actionProcessingId === row.product_id ? 'İşleniyor…' : 'AKTİFLEŞTİR'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                filteredPriceList.map((row) => {
                  const isSelected = selectedIds.includes(row.product_id)
                  const kdvPercent = Math.round(Number(row.kdv_rate) * 100)

                  return (
                    <tr key={row.product_id} className={isSelected ? 'row-selected' : ''}>
                      <td className="col-checkbox" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRow(row.product_id)}
                          aria-label={`${row.product_name} ürününü seç`}
                        />
                      </td>
                      <td className="col-action" style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={actionProcessingId === row.product_id}
                          onClick={() => handleUnassignProduct(row)}
                          title="Ürünü bu satış kanalından pasifleştir"
                          aria-label={`${row.product_name} ürününü pasifleştir`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '28px',
                            height: '28px',
                            padding: 0,
                            borderRadius: '6px',
                            color: '#dc2626',
                            borderColor: '#fca5a5',
                            background: '#fff',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fef2f2'
                            e.currentTarget.style.borderColor = '#dc2626'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#fff'
                            e.currentTarget.style.borderColor = '#fca5a5'
                          }}
                        >
                          {actionProcessingId === row.product_id ? (
                            <span style={{ fontSize: '10px' }}>…</span>
                          ) : (
                            <PowerIcon size={14} />
                          )}
                        </button>
                      </td>
                      {isColVisible('name') && (
                        <td className="col-name">
                          <strong>{row.product_name}</strong>
                        </td>
                      )}
                      {isColVisible('code') && (
                        <td className="col-code">
                          <code>{row.product_code}</code>
                        </td>
                      )}
                      {isColVisible('base_cost') && (
                        <td className="col-cost tabular-nums">
                          <strong>
                            {formatCurrencyAmount(Number(row.alis_fiyati ?? row.maliyet ?? 0), preferredCurr, CURRENCY_SYMBOLS)}
                          </strong>
                        </td>
                      )}
                      {altCurrencies.map((c) => {
                        if (!isColVisible(`base_cost_${c}`)) return null
                        const altVal = convertCurrency(Number(row.alis_fiyati ?? row.maliyet ?? 0), preferredCurr, c, effectiveRates)
                        return (
                          <td key={`base_cost_${c}`} className="col-cost tabular-nums">
                            <strong>
                              {formatCurrencyAmount(altVal, c, CURRENCY_SYMBOLS)}
                            </strong>
                          </td>
                        )
                      })}
                      {isColVisible('genel_maliyet') && (
                        <td className="col-cost tabular-nums">
                          <strong>
                            {formatCurrencyAmount(Number(row.genel_maliyet ?? 0), preferredCurr, CURRENCY_SYMBOLS)}
                          </strong>
                        </td>
                      )}
                      {altCurrencies.map((c) => {
                        if (!isColVisible(`genel_maliyet_${c}`)) return null
                        const altVal = convertCurrency(Number(row.genel_maliyet ?? 0), preferredCurr, c, effectiveRates)
                        return (
                          <td key={`genel_maliyet_${c}`} className="col-cost tabular-nums">
                            <strong>
                              {formatCurrencyAmount(altVal, c, CURRENCY_SYMBOLS)}
                            </strong>
                          </td>
                        )
                      })}

                      {/* Intermediate ancestor channel cells */}
                      {intermediateColumns.map((col) => {
                        const step = row.channel_steps?.find((s) => s.sales_channel_id === col.channelId)

                        if (col.type === 'price') {
                          const origPrice = Number(step?.kdv_haric_satis_fiyati ?? 0)
                          return (
                            <React.Fragment key={col.key}>
                              {isColVisible(`intermediate_${col.key}`) && (
                                <td className="col-price tabular-nums">
                                  {formatCurrencyAmount(origPrice, preferredCurr, CURRENCY_SYMBOLS)}
                                </td>
                              )}
                              {altCurrencies.map((c) => {
                                if (!isColVisible(`intermediate_${col.key}_${c}`)) return null
                                const altVal = convertCurrency(origPrice, preferredCurr, c, effectiveRates)
                                return (
                                  <td key={`intermediate_${col.key}_${c}`} className="col-price tabular-nums">
                                    {formatCurrencyAmount(altVal, c, CURRENCY_SYMBOLS)}
                                  </td>
                                )
                              })}
                            </React.Fragment>
                          )
                        }

                        if (!isColVisible(`intermediate_${col.key}`)) return null

                          // Margin column
                          const pm = step?.profit_margins?.find((m) => m.parameter_id === col.parameterId)
                          const effVal = pm?.value !== null && pm?.value !== undefined ? Number(pm.value) : null
                          const defVal = pm?.default_value !== null && pm?.default_value !== undefined ? Number(pm.default_value) : null
                          const isOv = Boolean(pm?.is_overridden)
                          const isApp = pm ? pm.is_applicable !== false : true

                          return (
                            <td key={col.key} className="col-margin">
                              <div
                                className="cell-editable"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                                onClick={() =>
                                  handleOpenMarginEdit(
                                    row,
                                    col.channelId,
                                    col.channelName,
                                    col.parameterId!,
                                    col.parameterLabel!,
                                    effVal,
                                    defVal,
                                    isOv,
                                    isApp,
                                  )
                                }
                                title={`${col.header} kâr marjını düzenle`}
                              >
                                <span
                                  className="tabular-nums"
                                  style={{
                                    fontWeight: isOv ? 700 : 400,
                                    color: isOv ? 'var(--color-text, #111827)' : 'inherit',
                                  }}
                                >
                                  {!isApp && !isOv ? '-' : effVal !== null ? `+${(effVal * 100).toFixed(1)}%` : '-'}
                                </span>
                                <button
                                  type="button"
                                  className="cell-edit-btn"
                                  aria-label={`${col.header} düzenle`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleOpenMarginEdit(
                                      row,
                                      col.channelId,
                                      col.channelName,
                                      col.parameterId!,
                                      col.parameterLabel!,
                                      effVal,
                                      defVal,
                                      isOv,
                                      isApp,
                                    )
                                  }}
                                >
                                  <PencilIcon />
                                </button>
                              </div>
                            </td>
                          )
                        })}

                      {/* Current channel Profit Margin Parameter Cells */}
                      {marginParams
                        .filter((mp) => isColVisible(`margin_${mp.id || mp.label}`))
                        .map((mp) => {
                          const mObj = row.profit_margins?.find((m) => m.parameter_id === mp.id)
                          const effVal = mObj?.value !== null && mObj?.value !== undefined ? Number(mObj.value) : null
                          const defVal = mObj?.default_value !== null && mObj?.default_value !== undefined ? Number(mObj.default_value) : (mp.value !== null ? Number(mp.value) : null)
                          const isOv = Boolean(mObj?.is_overridden)
                          const isApp = mObj ? mObj.is_applicable !== false : true

                          return (
                            <td key={mp.id || mp.label} className="col-margin">
                              <div
                                className="cell-editable"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  handleOpenMarginEdit(
                                    row,
                                    selectedChannelId,
                                    selectedChannel?.name,
                                    mp.id!,
                                    mp.label,
                                    effVal,
                                    defVal,
                                    isOv,
                                    isApp,
                                  )
                                }
                                title={`${mp.label} kâr marjını düzenle`}
                              >
                                <span
                                  className="tabular-nums"
                                  style={{
                                    fontWeight: isOv ? 700 : 400,
                                    color: isOv ? 'var(--color-text, #111827)' : 'inherit',
                                  }}
                                >
                                  {!isApp && !isOv ? '-' : effVal !== null ? `+${(effVal * 100).toFixed(1)}%` : '-'}
                                </span>
                                <button
                                  type="button"
                                  className="cell-edit-btn"
                                  aria-label={`${row.product_name} için ${mp.label} kâr marjını düzenle`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleOpenMarginEdit(
                                      row,
                                      selectedChannelId,
                                      selectedChannel?.name,
                                      mp.id!,
                                      mp.label,
                                      effVal,
                                      defVal,
                                      isOv,
                                      isApp,
                                    )
                                  }}
                                >
                                  <PencilIcon />
                                </button>
                              </div>
                            </td>
                          )
                        })}

                      {/* Toplam Kar (Read-only) */}
                      {isColVisible('toplam_kar') && (
                        <td className="col-margin tabular-nums">
                          <span style={{ fontWeight: 500 }}>
                            {formatProfitMargin(getRowToplamKar(row))}
                          </span>
                        </td>
                      )}

                      {isColVisible('kdv_haric') && (
                        <td className="col-price tabular-nums">
                          {formatCurrencyAmount(Number(row.kdv_haric_satis_fiyati ?? 0), preferredCurr, CURRENCY_SYMBOLS)}
                        </td>
                      )}
                      {altCurrencies.map((c) => {
                        if (!isColVisible(`kdv_haric_${c}`)) return null
                        const altVal = convertCurrency(Number(row.kdv_haric_satis_fiyati ?? 0), preferredCurr, c, effectiveRates)
                        return (
                          <td key={`kdv_haric_${c}`} className="col-price tabular-nums">
                            {formatCurrencyAmount(altVal, c, CURRENCY_SYMBOLS)}
                          </td>
                        )
                      })}
                      {channelIncludesKdv && isColVisible('kdv_dahil') && (
                        <td className="col-price tabular-nums">
                          {formatCurrencyAmount(Number(row.kdv_dahil_satis_fiyati ?? 0), preferredCurr, CURRENCY_SYMBOLS)}
                        </td>
                      )}
                      {channelIncludesKdv && altCurrencies.map((c) => {
                        if (!isColVisible(`kdv_dahil_${c}`)) return null
                        const altVal = convertCurrency(Number(row.kdv_dahil_satis_fiyati ?? 0), preferredCurr, c, effectiveRates)
                        return (
                          <td key={`kdv_dahil_${c}`} className="col-price tabular-nums">
                            {formatCurrencyAmount(altVal, c, CURRENCY_SYMBOLS)}
                          </td>
                        )
                      })}
                      {isColVisible('final_price') && (
                        <td className="col-price price-cell tabular-nums">
                          <strong style={{ color: 'var(--primary-color, #10b981)' }}>
                            {formatCurrencyAmount(Number(row.final_satis_fiyati ?? row.satis_fiyati ?? 0), preferredCurr, CURRENCY_SYMBOLS)}
                          </strong>
                        </td>
                      )}
                      {altCurrencies.map((c) => {
                        if (!isColVisible(`final_price_${c}`)) return null
                        const altVal = convertCurrency(Number(row.final_satis_fiyati ?? row.satis_fiyati ?? 0), preferredCurr, c, effectiveRates)
                        return (
                          <td key={`final_price_${c}`} className="col-price price-cell tabular-nums">
                            <strong style={{ color: 'var(--primary-color, #10b981)' }}>
                              {formatCurrencyAmount(altVal, c, CURRENCY_SYMBOLS)}
                            </strong>
                          </td>
                        )
                      })}
                      {channelIncludesKdv && isColVisible('kdv_rate') && (
                        <td className="col-kdv tabular-nums">%{kdvPercent}</td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Single Margin Override Modal */}
      {marginEdit && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '440px', width: '90%' }}>
            <h3>
              {marginEdit.salesChannelName ? `${marginEdit.salesChannelName} — ` : ''}
              {marginEdit.label} Düzenle
            </h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              <strong>{marginEdit.row.product_name}</strong> ({marginEdit.row.product_code}) için kâr marjı değerini girin.
            </p>

            <form onSubmit={handleSaveMarginEdit}>
              <div className="form-group">
                <label>Kâr Marjı Yüzdesi (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  autoFocus
                  placeholder="Örn: 20"
                  value={marginEditInput}
                  onChange={(e) => setMarginEditInput(e.target.value)}
                />
                <span className="helper-text">
                  {marginEdit.defaultValue !== null
                    ? `Kanal Varsayılanı: %${(marginEdit.defaultValue * 100).toFixed(1)}`
                    : 'Kategori filtresi eşleşmediği için varsayılan marj tanımlı değil (-). Özel bir kâr marjı belirleyebilirsiniz.'}
                </span>
              </div>

              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  {marginEdit.isOverridden && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleResetSingleMargin}
                      title="Özel kâr marjını silip varsayılan değere döndürür"
                    >
                      Varsayılana Sıfırla
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setMarginEdit(null)}>
                    İptal
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Kaydet
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Margin Override Modal */}
      {showBulkMarginModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '480px', width: '90%' }}>
            <h3>Toplu Kâr Marjı Düzenle</h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              Seçilen <strong>{visibleSelectedIds.length}</strong> ürün için kâr marjı değerini toplu olarak güncelleyin.
            </p>

            <form onSubmit={handleApplyBulkMargin}>
              <div className="form-group">
                <label>Hedef Parametre *</label>
                <select
                  value={bulkParameterId}
                  onChange={(e) => setBulkParameterId(e.target.value)}
                >
                  {marginParams.map((mp) => (
                    <option key={mp.id} value={mp.id}>
                      {mp.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>İşlem Türü</label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.25rem' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="bulkMode"
                      value="set"
                      checked={bulkMode === 'set'}
                      onChange={() => setBulkMode('set')}
                    />
                    <span>Yeni Değer Belirle</span>
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="bulkMode"
                      value="reset"
                      checked={bulkMode === 'reset'}
                      onChange={() => setBulkMode('reset')}
                    />
                    <span>Kanal Varsayılanına Sıfırla</span>
                  </label>
                </div>
              </div>

              {bulkMode === 'set' && (
                <div className="form-group">
                  <label>Yeni Kâr Marjı Yüzdesi (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="Örn: 25"
                    value={bulkMarginValue}
                    onChange={(e) => setBulkMarginValue(e.target.value)}
                  />
                  <span className="helper-text">0 ile 100 arasında bir yüzde girin (örn: %25 için 25).</span>
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkMarginModal(false)}
                  disabled={bulkProcessing}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={bulkProcessing}
                >
                  {bulkProcessing ? 'İşleniyor…' : 'Uygula'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Column Visibility Modal */}
      {showColumnModal && (
        <div className="modal-backdrop" onClick={() => setShowColumnModal(false)}>
          <div
            className="modal-card"
            style={{ maxWidth: '440px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Sütun Görünürlüğü</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowColumnModal(false)}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  color: 'var(--text-muted, #6b7280)',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              Fiyat listesi tablosunda görüntülemek istediğiniz sütunları işaretleyin.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleShowAllColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Seç
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleHideAllColumns}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Tümünü Kaldır
              </button>
            </div>

            <div
              style={{
                maxHeight: '340px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {allColumns.map((col) => {
                const checked = isColVisible(col.key)
                return (
                  <label
                    key={col.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '7px 10px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: checked ? 'transparent' : '#f9fafb',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = checked ? 'transparent' : '#f9fafb')
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleColumn(col.key)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    <span
                      style={{
                        fontSize: '0.875rem',
                        color: checked ? '#111827' : '#9ca3af',
                        fontWeight: checked ? 500 : 400,
                      }}
                    >
                      {col.label}
                    </span>
                  </label>
                )
              })}
            </div>

            <div className="modal-actions" style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowColumnModal(false)}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
